import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardSection,
  SmallHeader,
  Gutter,
  FocusAreaBars,
  CandidateHighlightExperienceSummary,
  CandidateHighlightExperienceDetails,
  useApi,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';

import { CandidateProfileHighlightsApi } from '../../api/candidate-profile-highlights-api';
import { CandidateProfileTopFocusAreasApi } from '../../api/candidate-profile-top-focus-areas-api';
import { TaxonomyApi } from '../../api/taxonomy-api';

import { ProfileHighlightsFocusAreaBarsSubtitle } from './ProfileHighlightsFocusAreaBarsSubtitle';
import { ProfileHighlightsTopFocusAreasModal } from './ProfileHighlightsTopFocusAreasModal';

export type ProfileHighlightsProps = {
  name?: string;
  candidate: Candidate;
};

export const ProfileHighlights = ({
  name = 'PROFILE_HIGHLIGHTS',
  candidate,
}: ProfileHighlightsProps) => {
  const [
    { data: profileHighlights },
    { data: profileTopFocusAreas },
    { data: taxonomy },
  ] = useApi(
    CandidateProfileHighlightsApi.readProfileHighlights(candidate.id),
    CandidateProfileTopFocusAreasApi.readProfileTopFocusAreas(candidate.id),
    TaxonomyApi.readTaxonomy()
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasTopFocusAreas = profileHighlights.topFocusAreas.length > 0;
  const focusAreas = profileHighlights.topFocusAreas.map(fa => {
    return {
      ...fa,
      name: taxonomy.focusAreas[fa.focusArea]?.fullName ?? 'Other',
    };
  });

  const handleOnEditClick = hasTopFocusAreas
    ? () => {
        setIsModalOpen(true);
      }
    : undefined;

  return (
    <>
      <Card name={name}>
        <CardHeader name="pendo-profile-highlights-header">
          <SmallHeader>Highlights</SmallHeader>
        </CardHeader>
        <CardSection>
          <CandidateHighlightExperienceSummary
            candidate={profileHighlights}
            taxonomy={taxonomy}
          />
          <Gutter bottom="8px" />
          <FocusAreaBars
            title="Top Focus Areas"
            focusAreas={focusAreas}
            onEditClick={handleOnEditClick}
            subtitle={
              hasTopFocusAreas ? (
                <ProfileHighlightsFocusAreaBarsSubtitle candidate={candidate} />
              ) : null
            }
          />
          <Gutter bottom="24px" />
          <CandidateHighlightExperienceDetails
            candidate={profileHighlights}
            taxonomy={taxonomy}
            showPracticeArea
          />
        </CardSection>
      </Card>
      {isModalOpen ? (
        <ProfileHighlightsTopFocusAreasModal
          candidate={candidate}
          onClose={() => {
            setIsModalOpen(false);
          }}
          profileTopFocusAreas={profileTopFocusAreas}
          taxonomy={taxonomy}
        />
      ) : null}
    </>
  );
};
